import { zodResolver } from '@hookform/resolvers/zod'
import type { AgencyGraphJson, AgencyResponse } from '@saaga/shared/interfaces/agency.interface'
import { useEffect, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'
import {
  type AgencyFormSchemaType,
  agencyFormSchema,
  defaultAgencyFormValues,
} from '@/components/agencies/agency-form.schema'
import { serializeFlowData } from './agency-flow-serialize.util'
import {
  extractAgencyGraphJsonFromResponse,
  mapResponseToFormValues,
} from './agency-form-mapper.util'

export interface UseAgencyFormInitReturn {
  form: ReturnType<typeof useForm<AgencyFormSchemaType>>
  flowDataRef: React.MutableRefObject<AgencyGraphJson>
  initialFlowSnapshotRef: React.MutableRefObject<string>
}

export const useAgencyFormInit = (
  agencyResponse: AgencyResponse | null
): UseAgencyFormInitReturn => {
  const flowDataRef = useRef<AgencyGraphJson>({ nodeList: [], edgeList: [] })
  const initialFlowSnapshotRef = useRef<string>(serializeFlowData({ nodeList: [], edgeList: [] }))

  const initialValues = useMemo(() => {
    if (agencyResponse) {
      const graphJson = extractAgencyGraphJsonFromResponse(agencyResponse)
      flowDataRef.current = graphJson
      initialFlowSnapshotRef.current = serializeFlowData(graphJson)
      return { ...defaultAgencyFormValues, ...mapResponseToFormValues(agencyResponse) }
    }
    return defaultAgencyFormValues
  }, [agencyResponse])

  const form = useForm<AgencyFormSchemaType>({
    resolver: zodResolver(agencyFormSchema),
    defaultValues: initialValues,
    mode: 'onBlur',
  })

  useEffect(() => {
    if (agencyResponse) {
      form.reset({ ...defaultAgencyFormValues, ...mapResponseToFormValues(agencyResponse) })
      const graphJson = extractAgencyGraphJsonFromResponse(agencyResponse)
      flowDataRef.current = graphJson
      initialFlowSnapshotRef.current = serializeFlowData(graphJson)
    }
  }, [agencyResponse, form])

  return { form, flowDataRef, initialFlowSnapshotRef }
}
